import type { WireCard } from "@app/protocol";
import { backImage, CARD_RADIUS, cardImage, type Deck, deckImages, deckOf } from "@app/ui";
import { useEffect } from "react";
import { useStore } from "../store";

export const SUIT_GLYPH: Record<WireCard["suit"], string> = {
  spades: "♠",
  hearts: "♥",
  diamonds: "♦",
  clubs: "♣",
};
const FACE: Record<number, string> = { 11: "J", 12: "Q", 13: "K", 14: "A" };

export function rankLabel(rank: number): string {
  return FACE[rank] ?? String(rank);
}

/** Suit by glyph and letter, never colour alone (review 0004 U14). */
export function cardLabel(card: WireCard): string {
  return `${rankLabel(card.rank)}${SUIT_GLYPH[card.suit]}`;
}

/** The table's deck: the owner's choice, the same for everyone at the table (ADR 0029). */
function useTableDeck(): Deck {
  return deckOf(useStore((s) => s.snapshot?.table.deck));
}

/**
 * Fetch the table's whole deck once, so no card is blank the first time it is dealt. Only once
 * the table has said which deck it plays: guessing the default first meant 53 requests for a deck
 * the table might not use, and 53 more for the real one (review 0027 G4).
 */
export function useDeckPreload() {
  const named = useStore((s) => s.snapshot?.table.deck);
  useEffect(() => {
    if (named === undefined) return;
    for (const src of deckImages(deckOf(named))) new Image().src = src;
  }, [named]);
}

/**
 * A card face: the deck's picture (ADR 0024), named for screen readers.
 * `on` glows faintly teal; `off` is darkened.
 */
export function Card({
  card,
  width,
  height,
  state,
  onPlay,
  pressable = false,
  pressed,
  className = "",
}: {
  card: WireCard;
  width: number;
  height: number;
  state: "on" | "off" | "plain";
  onPlay?: (card: WireCard) => void;
  /** A card that cannot be played is still pressable: the table answers with the rule (decision 172). */
  pressable?: boolean;
  /** A card chosen among others (Hearts' pass): a toggle, said as such. */
  pressed?: boolean | undefined;
  className?: string;
}) {
  // Playable cards glow faintly; the others darken but stay opaque, so an
  // overlapping card never shows the one under it (owner, 11 Sept).
  const deck = useTableDeck();
  const look =
    state === "on"
      ? "shadow-card-on"
      : state === "off"
        ? "shadow-card brightness-[0.6] grayscale-[0.55]"
        : "shadow-card";
  return (
    <button
      type="button"
      className={`relative block bg-card transition-[transform,filter,box-shadow] duration-150 ease-out ${look} ${className}`}
      style={{ width, height, borderRadius: CARD_RADIUS }}
      disabled={onPlay === undefined || (state !== "on" && !(pressable && state === "off"))}
      // Pressable but not playable: the press only asks the table why (decision 172).
      aria-disabled={state === "off" ? true : undefined}
      aria-pressed={pressed}
      aria-label={cardLabel(card)}
      onClick={() => onPlay?.(card)}
    >
      <img
        src={cardImage(card, deck)}
        alt=""
        draggable={false}
        className="block h-full w-full select-none"
      />
    </button>
  );
}

/** The back of a card: the deck's back picture. */
export function CardBack({ width, height }: { width: number; height: number }) {
  const deck = useTableDeck();
  return (
    <img
      src={backImage(deck)}
      alt=""
      aria-hidden="true"
      draggable={false}
      className="block select-none shadow-card"
      style={{ width, height, borderRadius: CARD_RADIUS }}
    />
  );
}
